import React, { useEffect, useState, useRef, useMemo } from "react";
import "../components/PatientDetails.css";
import { useNavigate } from "react-router-dom";
import { FaFileInvoice, FaCheckCircle, FaEdit, FaPrint, FaTrash } from "react-icons/fa";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logoImage from '../assets/awasthi logo 1.jpg';

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, User, MapPin, Users } from "lucide-react";
import { Pencil } from "lucide-react";
import axios from "axios";
import { hasPermission, clearPermissionCache } from "../utils/permissions";

const PatientDetails = () => {
  const navigate = useNavigate();
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  // View permission is disabled for this page (always allowed)
  const [canView, setCanView] = useState(true);
  const [canViewReport, setCanViewReport] = useState(false);
  const [canAddReport, setCanAddReport] = useState(false);
  const [canDeleteReport, setCanDeleteReport] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(true);
  const { patient_id } = useParams();
  const [courierOptions, setCourierOptions] = useState([]);
  const [newDelivery, setNewDelivery] = useState({ courier_id: "", tracking_no: "", delivery_date: "", contact_name: "", contact_mobile: "" });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [canSeeNotepad, setCanSeeNotepad] = useState(false);
  const [courses, setCourses] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [prescriptionDate, setPrescriptionDate] = useState("");

  // --- Permission for Invoice editing and deleting (see below) ---
  // dont remove comment it -> invoice permission keys match backend: invoiceeditview, invoicedeleteview
  // This reflects backend logic from: AdminController::getAllowedPermissionsMap()
  const [canEditInvoice, setCanEditInvoice] = useState(false);
  const [canDeleteInvoice, setCanDeleteInvoice] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const notepad = await hasPermission("folders", "notepadview");
        setCanSeeNotepad(!!notepad);
        // --------- Invoice edit permission check here ---------
        // dont remove comment it -> key must match DB value: 'invoiceeditview' (not 'editview')
        const allowEditInvoice = await hasPermission("invoice", "invoiceeditview");
        setCanEditInvoice(!!allowEditInvoice);
        // --------- Invoice delete permission check here ---------
        const allowDeleteInvoice = await hasPermission("invoice", "invoicedeleteview");
        setCanDeleteInvoice(!!allowDeleteInvoice);
        // -----------------------------------------------------
      } catch (err) {
        console.error("Error loading permissions:", err);
        setCanEditInvoice(false);
        setCanDeleteInvoice(false);
      }
    };

    loadPermissions();

    const handlePermissionUpdate = () => loadPermissions();
    window.addEventListener("permissionsUpdated", handlePermissionUpdate);

    return () =>
      window.removeEventListener("permissionsUpdated", handlePermissionUpdate);
  }, []);

  useEffect(() => {
    const loadPerms = async () => {
      try {
        // Clear cached permissions to ensure fresh check
        clearPermissionCache();

        const createPerm = await hasPermission('operation', 'addpatiententry');
        const hasEditEntry = await hasPermission('operation', 'editpatiententry');
        const hasEditPatient = await hasPermission('operation', 'editpatient');
        const editPerm = hasEditEntry || hasEditPatient;

        // View permission is not required on this page
        const viewPerm = true;
        // Report permissions
        const hasViewRep = await hasPermission('report', 'viewreport');
        const hasAddRep = await hasPermission('report', 'addreport');
        const hasDelRep = await hasPermission('report', 'deletereport');
        setCanViewReport(!!hasViewRep);
        setCanAddReport(!!hasAddRep);
        setCanDeleteReport(!!hasDelRep);

        // Delete permission: allow either 'operation.deletepatient' or 'operation.deletepatiententry'
        const hasDeletePatient = await hasPermission('operation', 'deletepatient');
        const hasDeletePatientEntry = await hasPermission('operation', 'deletepatiententry');
        const deletePerm = hasDeletePatient || hasDeletePatientEntry;

        console.log('PatientDetails permission check:', {
          create: createPerm,
          edit: editPerm,
          view: viewPerm,
          delete: deletePerm,
          hasDeletePatient,
          hasDeletePatientEntry
        });

        setCanCreate(createPerm);
        setCanEdit(editPerm);
        setCanView(true);
        setCanDelete(deletePerm);
        // --- Invoice edit already handled in the above loadPermissions ----
      } catch (error) {
        console.error('Error loading permissions:', error);
        // On error, deny all permissions
        setCanCreate(false);
        setCanEdit(false);
        setCanView(false);
        setCanDelete(false);
        setCanEditInvoice(false); // fail-safe
        setCanDeleteInvoice(false); // fail-safe
      } finally {
        setPermissionsLoaded(true);
      }
    };
    loadPerms();
  }, []);

  // Remove unused couriers preload to avoid extra network cost
  useEffect(() => {
    // Intentionally no-op: previous couriers preload removed for performance
  }, []);

  const handleCreateDelivery = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token') || '';
    try {
      await axios.post(`https://newerp.awasthivitality.com/public/api/patients/${patient_id}/deliveries`, newDelivery, { headers: { Authorization: `Bearer ${token}` } });
      setNewDelivery({ courier_id: "", tracking_no: "", delivery_date: "", contact_name: "", contact_mobile: "" });
      alert('Delivery added');
      // Minimal refresh by reloading patient details; relying on existing loader higher up if any
      // Alternatively push into courierTracking UI list
    } catch (err) {
      console.error(err);
      alert('Failed to add delivery');
    }
  };
  // 1️⃣ states
  const [dateSortOrder, setDateSortOrder] = useState("asc");

  const sortedCourses = [...courses].sort((a, b) => {
    const dateA = new Date(a.course_date || a.date_formatted);
    const dateB = new Date(b.course_date || b.date_formatted);

    return dateSortOrder === "asc"
      ? dateA - dateB
      : dateB - dateA;
  });

  const handleDeleteCourier = async (delivery_id) => {
    if (!delivery_id) {
      toast.error("Delivery ID not found");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this courier?")) return;

    try {
      const token = (localStorage.getItem("session_token") || "").trim();

      const response = await axios.post(
        `https://newerp.awasthivitality.com/public/api/delivery/delete?delivery_id=${delivery_id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        toast.success(response.data.message || "Deleted successfully");

        // ✅ Remove from UI instantly
        setCourierTracking((prev) =>
          prev.filter((item) => item.delivery_id !== delivery_id)
        );
      } else {
        toast.error(response.data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting courier");
    }
  };
  const handleAddNewClick = () => {
    navigate(`/RelativesPage/${patient_id}`);
  };
  const [patient, setPatient] = useState(null);
  // const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [courierTracking, setCourierTracking] = useState([]);
  const [videos, setVideos] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [currentStatusMap, setCurrentStatusMap] = useState({});
  const [relativesFromAPI, setRelativesFromAPI] = useState([]);
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // ✅ MULTIPLE REPORT UPLOAD STATES (ADD THIS)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const [confirmUpload, setConfirmUpload] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteComment, setNoteComment] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  // Root used to build media URLs like in CourierEntryEdit: API_BASE.replace('/api','')
  const API_ROOT = 'https://newerp.awasthivitality.com';

  // Report file handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setSelectedFiles(files);

    // preview only images
    const previews = files.map((file) =>
      file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null
    );

    setPreviewUrls(previews);
    setConfirmUpload(true);
  };
  const getReportFileUrl = (report) => {
    const API_ROOT = "https://newerp.awasthivitality.com";

    if (report.file_url) {
      // already absolute
      if (report.file_url.startsWith("http")) return report.file_url;

      // relative path
      return `${API_ROOT}/${report.file_url.replace(/^\/+/, "")}`;
    }

    if (report.file_name) {
      return `${API_ROOT}/uploads/patient_mri/${encodeURIComponent(report.file_name)}`;
    }

    return "";
  };
  const isPdfFile = (name = "", url = "") =>
    /\.pdf$/i.test(name) || /\.pdf$/i.test(url);

  const isAipexUrl = (url = "") =>
    url.includes("aipexworldwide.com/live/V2/config/pdf");
  const handleUpload = async () => {
    try {
      if (!canAddReport) {
        alert("No permission");
        return;
      }

      if (selectedFiles.length === 0) {
        alert("Select files first");
        return;
      }

      const token = localStorage.getItem("session_token") || "";
      const formData = new FormData();

      formData.append("patient_id", patient_id);

      // 🔥 VERY IMPORTANT (files[])
      selectedFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const response = await fetch(
        `https://newerp.awasthivitality.com/api/patient/report/add`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Session-Token": token,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!result.success) {
        alert(result.msg || "Upload failed");
        return;
      }

      // 🔥 backend returns array
      // setReports((prev) => [...result.data, ...prev]);
      
      setSelectedFile(null);
      setPreviewUrl(null);
      setSelectedFiles([]);
      setPreviewUrls([]);
      setConfirmUpload(false);
      toast.success( "Reports uploaded");
      await reloadReports();

      // toast.success(result.msg || "Reports uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }
  };


  const handleEdit = (patient_course_id) => {
    if (!patient_course_id) {
      alert("Course ID not found!");
      return;
    }
    // Navigate to the Edit page with the course ID
    navigate(`/EditMorePatient/${patient_course_id}`);
  };
  const [showFullMobile, setShowFullMobile] = useState(false);
  const [showFullWa, setShowFullWa] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyNumber = (number, field) => {
    if (!number) return;

    navigator.clipboard.writeText(number).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(err => {
      console.error("Copy failed", err);
    });
  };

  // ✅ Helper: Mask last 5 digits of phone number and reveal on hover
  const maskPhoneNumber = (number) => {
    if (!number) return "—";
    const visible = number.slice(0, -5);
    const hidden = number.slice(-5);
    return (
      <span className="masked-phone" title={number}>
        {visible + "*****"}
      </span>
    );
  };
  const handleDeletePhoto = async () => {
    const id = patient?.doc_id;   // ✅ get id from patient

    console.log("Deleting ID:", id);

    if (!id) {
      alert("doc_id not found!");
      return;
    }

    try {
      const res = await fetch(
        `https://newerp.awasthivitality.com/public/api/patient/photo/remove/${id}`,
        {
          method: "DELETE", // IMPORTANT
        }
      );

      const result = await res.json();
      console.log("API Response:", result);

      if (result.success) {
        alert("Photo deleted successfully");

        setPatient((prev) => ({
          ...prev,
          patient_photo: null,
        }));
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error(error);
      alert("Error deleting photo");
    }
  };
  const handleDeleteReport = async (doc_id) => {
    if (!canDeleteReport) {
      alert('You do not have permission to delete reports.');
      return;
    }

    if (!window.confirm("Are you sure you want to delete this report?")) return;

    console.log("Attempting to delete report with doc_id:", doc_id);

    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      if (!token) {
        alert('Unauthorized: Please log in again.');
        return;
      }

      // --- Axios DELETE request ---
      const url = `https://newerp.awasthivitality.com/api/patient/report/delete/${doc_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await axios.delete(
        url,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(token ? { 'X-Session-Token': token } : {}),
          },
        }
      );

      console.log("API response:", response);

      // --- Check if success ---
      if (response.data && response.data.success) {
        console.log("Report deleted successfully:", response.data);

        // Remove the deleted report from state
        setReports((prev) => prev.filter((r) => r.doc_id !== doc_id && r.id !== doc_id));

        toast.success(response.data.msg || "Report deleted successfully!");
      } else {
        console.warn("API returned failure:", response.data);
        toast.error(response.data.msg || "Failed to delete report.");
      }

    } catch (err) {
      console.error("Delete error caught:", err);

      if (err.response) {
        // Server responded with error
        console.error("Status:", err.response.status);
        console.error("Response data:", err.response.data);
        toast.error(`Error ${err.response.status}: ${err.response.data.msg || 'Failed to delete report'}`);
      } else if (err.request) {
        // Request made but no response
        console.error("No response received:", err.request);
        toast.error("No response from server. Check your network or CORS.");
      } else {
        // Something else
        console.error("Error setting up request:", err.message);
        toast.error(`Error: ${err.message}`);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      alert('You do not have permission to delete patient courses.');
      return;
    }

    const confirmDelete = window.confirm("Are you sure you want to delete?");
    if (!confirmDelete) return;

    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = `https://newerp.awasthivitality.com/api/patient-course/delete/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        }
      });

      const result = await response.json();

      if (response.status === 403) {
        alert(result.message || result.msg || "You do not have permission to delete patient courses.");
        return;
      }

      if (result.success) {
        alert("Deleted successfully");
        setCourses((prev) => prev.filter((course) => course.patient_course_id !== id));
      } else {
        alert("Failed to delete: " + (result.msg || "Unknown error"));
      }
    } catch (err) {
      console.error("Error deleting:", err);
      alert("Error deleting. Check console.");
    }
  };


  const handlePdf = (id) => {
    console.log("Download PDF for courier", id);
    // generate or open PDF
  };

  const authHeaders = () => {
    const token = localStorage.getItem('session_token') || '';
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    axios
      .get("https://newerp.awasthivitality.com/api/courier-status-options", { headers: authHeaders() })
      .then((res) => {
        if (res.data.success) {
          setStatusOptions(res.data.data.status_options);
          setCurrentStatusMap(res.data.data.current_statuses);
        }
      })
      .catch((err) => console.error(err));
  }, []);
  // Ref for file input
  const fileInputRef = useRef(null);

  const RelativesList = ({ patientId, initialRelatives = [] }) => {
    const [relatives, setRelatives] = useState(initialRelatives || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const handleDeleteRelatives = async (relative_id) => {
      if (!window.confirm("Are you sure you want to delete this relative?")) return;

      try {
        console.log("=== DELETE RELATIVE DEBUG ===");
        console.log("Patient ID:", patientId);
        console.log("Relative ID (relationship ID):", relative_id);

        // Find the relative record to get the relative patient ID
        const relativeRecord = relatives.find(rel => rel.relative_id === relative_id);
        if (!relativeRecord) {
          alert("Relative record not found");
          return;
        }

        // Get the relative patient ID (the actual patient ID of the relative)
        const relativePatientId = relativeRecord.patient_id; // This is the relative's patient ID
        console.log("Relative Patient ID:", relativePatientId);

        // ✅ Use the correct bidirectional delete endpoint
        // Route: /relative/remove/{patient_id}/{relative_id}
        // Where relative_id is actually the relative patient's ID
        const response = await axios.delete(
          `https://newerp.awasthivitality.com/api/relative/remove/${patientId}/${relativePatientId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log("Delete response:", response);
        console.log("Response status:", response.status);
        console.log("Response data:", response.data);

        if (response.data.success) {
          // ✅ Remove from local state immediately
          setRelatives((prev) => prev.filter((rel) => rel.relative_id !== relative_id));
          alert("Relative deleted successfully from both sides");
        } else {
          alert("Failed to delete relative: " + (response.data.message || "Unknown error"));
        }
      } catch (error) {
        console.error("=== DELETE ERROR DETAILS ===");
        console.error("Full error object:", error);

        if (error.response) {
          console.error("Response status:", error.response.status);
          console.error("Response data:", error.response.data);

          if (error.response.status === 500) {
            const errorMsg = error.response.data?.message || 'Internal server error';
            alert(`Server Error (500): ${errorMsg}`);
          } else {
            alert(`Delete failed: ${error.response.data?.message || error.response.statusText}`);
          }
        } else if (error.request) {
          alert("Delete failed: No response from server. Check network connection.");
        } else {
          alert("Delete failed: " + error.message);
        }
      }
    };
    // Use initialRelatives if provided, otherwise fetch
    useEffect(() => {
      if (initialRelatives && initialRelatives.length > 0) {
        setRelatives(initialRelatives);
        setLoading(false);
        return;
      }

      // Only fetch if no initial data provided
      const fetchRelatives = async () => {
        setLoading(true);
        try {
          const token = (localStorage.getItem('session_token') || '').trim();
          const url = `https://newerp.awasthivitality.com/api/patient/details/${patient_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
          const res = await fetch(url, {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
              ...(token ? { 'X-Session-Token': token } : {}),
            }
          });
          const data = await res.json();

          if (data.success && data.data?.relatives) {
            setRelatives(data.data.relatives);
          } else {
            setRelatives([]);
          }
        } catch (err) {
          console.error(err);
          setError("Failed to fetch relatives.");
        } finally {
          setLoading(false);
        }
      };

      fetchRelatives();
    }, [patient_id, initialRelatives]);

    if (loading) return <p>Loading relatives...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (relatives.length === 0)
      return <p style={{ color: "#666" }}>No relatives found.</p>;

    return (
      <ul style={{ marginTop: "10px", listStyle: "none", paddingLeft: 0 }}>
        {relatives.map((rel) => (
          <li
            key={rel.relative_id}
            style={{
              background: "#f9f9f9",
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "8px 12px",
              marginBottom: "6px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>{rel.name}</strong> <br />
              <small>ID: {rel.patient_id}</small>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => navigate(`/patient/details/${rel.patient_id}`)}
                style={{
                  background: "#00c3e1",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "0.3s",
                }}
              >
                View
              </button>

              <button
                onClick={() => handleDeleteRelatives(rel.relative_id)}
                style={{
                  background: "#ff4d4d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: "26px",
                  height: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "13px",
                  transition: "0.3s",
                }}
                title="Delete"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>

    );
  };
  const handlePrintPrescription = (selectedDate) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const topMargin = 65;
    const bottomMargin = 50;
    let currentY = topMargin;
    const leftX = 14;
    const rightX = 110;
    const lineSpacing = 6;

    // Format date helper function
    const formatDate = (dateString) => {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      } catch (error) {
        // If date is already formatted or in different format, return as is
        return dateString;
      }
    };

    // Get course_date from patient or latest course
    let courseDate = patient?.course_date || patient?.date_formatted || null;

    // If not in patient object, try to get from latest course
    if (!courseDate && courses && courses.length > 0) {
      // Get the most recent course (first one in the array, as they're sorted by date desc)
      const latestCourse = courses[0];
      courseDate = latestCourse?.course_date || latestCourse?.date_formatted || null;
    }

    // --- Patient Details Only ---
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const addField = (label, value, x, y) => {
      if (y > pageHeight - bottomMargin) {
        doc.addPage();
        y = topMargin;
      }
      doc.setFont("helvetica", "bold");
      doc.text(label, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(value || "—", x + 35, y);
      return y;
    };

    // Left Column
    // ✅ FIXED: Use course_date instead of today's date
    // currentY = addField("Date:", formatDate(new Date()), leftX, currentY);
    const finalDate = selectedDate ? new Date(selectedDate) : new Date();
    currentY = addField("Date:", formatDate(finalDate), leftX, currentY);

    currentY += lineSpacing;
    currentY = addField("Patient ID:", patient.pr_no, leftX, currentY);
    currentY += lineSpacing;
    currentY = addField(
      "Patient Name:",
      `${patient.name || "—"} / ${patient.age || "—"} / ${patient.sex_text || "—"}`,
      leftX,
      currentY
    );
    currentY += lineSpacing;
    currentY = addField("Mobile No:", patient.mobile, leftX, currentY);
    currentY += lineSpacing;
    currentY = addField("Whatsapp No:", patient.mobile2, leftX, currentY);
    currentY += lineSpacing;
    currentY = addField("State:", patient.state, leftX, currentY);

    // Right Column
    let rightY = topMargin;
    rightY = addField("BP:", patient.bp_text, rightX, rightY);
    rightY += lineSpacing;
    rightY = addField("Diabetes:", patient.diabetes_text, rightX, rightY);
    rightY += lineSpacing;
    rightY = addField("Thyroid:", patient.thyroid_text, rightX, rightY);
    rightY += lineSpacing;
    rightY = addField("Height:", patient.height, rightX, rightY);
    rightY += lineSpacing;
    rightY = addField("Weight:", patient.weight, rightX, rightY);
    rightY += lineSpacing;
    rightY = addField("City:", patient.city, rightX, rightY);

    // --- Full-width bottom line ---
    const finalY = Math.max(currentY, rightY) + 5; // spacing below last field
    doc.setLineWidth(0.5); // line thickness
    doc.line(0, finalY, pageWidth, finalY); // full-width horizontal line

    // --- Fast Download ---
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${patient.name}_Prescription.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchPatientDetails = async () => {
    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      if (!token) {
        setError("Unauthorized: Please log in again.");
        setLoading(false);
        return;
      }

      const url = `https://newerp.awasthivitality.com/api/patient/view/${patient_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Session-Token': token,
        }
      });

      const result = await response.json().catch(() => ({
        success: false,
        message: 'Invalid response from server'
      }));

      if (response.status === 403) {
        setError(result.message || result.msg || "You do not have permission to view patient details.");
        setLoading(false);
        return;
      }

      if (response.status === 401) {
        setError(result.message || result.msg || "Unauthorized: Please log in again.");
        setLoading(false);
        return;
      }

      if (response.status === 404) {
        setError(result.message || result.msg || "Patient not found.");
        setLoading(false);
        return;
      }

      if (response.status === 500 || !response.ok) {
        setError(result.message || result.msg || "Server error. Please try again.");
        setLoading(false);
        return;
      }

      if (result.success && result.data?.patient) {
        setPatient(result.data.patient);

        // Courses
        let courseList = [];
        if (Array.isArray(result.data.patient_courses)) {
          courseList = result.data.patient_courses;
        } else if (Array.isArray(result.data.courses)) {
          courseList = result.data.courses;
        } else if (Array.isArray(result.data.course_data)) {
          courseList = result.data.course_data;
        }
        setCourses(courseList);

        // ✅ Extract invoice data from the response (already included)
        // ✅ CORRECT invoice extraction based on backend response
        let invoiceList = [];

        if (Array.isArray(result.data?.invoices?.invoices)) {
          invoiceList = result.data.invoices.invoices;
        }

        setInvoices(invoiceList);

        console.log("FINAL INVOICE LIST 👉", invoiceList);

        // Reports
        if (result.data.reports?.documents) {
          setReports(result.data.reports.documents);
        }

        // Store relatives - they come from result.data.relatives
        if (result.data.relatives && Array.isArray(result.data.relatives)) {
          // Store in a separate state to pass to RelativesList
          setRelativesFromAPI(result.data.relatives);
        }
        
        const trackingData = Array.isArray(result.data?.courier_tracking?.data)
          ? result.data.courier_tracking.data
          : null;

        if (Array.isArray(trackingData)) {
          const courierList = trackingData.map((d) => {
            const fileName = d.customer_details && d.customer_details !== 'N/A' ? d.customer_details : '';
            const relPath = d.invoice || d.pdf_file || '';
            // Match CourierEntryEdit preview logic: prefer API_ROOT/relPath; otherwise uploads/delivery/name
            const candidates = [];
            const pushUniq = (u) => { if (u && !candidates.includes(u)) candidates.push(u); };
            if (relPath) {
              pushUniq(`${API_ROOT}/${String(relPath).replace(/^\/+/, '')}`);
            }
            if (fileName) {
              pushUniq(`${API_ROOT}/uploads/delivery/${encodeURIComponent(fileName)}`);
              pushUniq(`${API_ROOT}/uploads/delivery/${fileName}`);
            }
            let fileUrl = "";

            // ✅ Case 1: Aipex URL
            if (d.customer_details && d.customer_details.includes("aipexworldwide.com")) {
              fileUrl = d.customer_details;
            }

            // ✅ Case 2: Manual uploaded PDF (VERY IMPORTANT FIX)
            else if (d.customer_details && d.customer_details.endsWith(".pdf")) {
              fileUrl = `${API_ROOT}/uploads/delivery/${d.customer_details}`;
            }

            // ✅ Case 3: fallback
            else {
              fileUrl = candidates[0] || "";
            }
            const fileUrlAlt = candidates[1] || '';
            const fileUrlAlt2 = candidates[2] || '';
            const mimeGuess = (() => {
              const n = (fileName || relPath || '').toLowerCase();
              if (n.endsWith('.pdf')) return 'application/pdf';
              if (n.endsWith('.png')) return 'image/png';
              if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
              if (n.endsWith('.webp')) return 'image/webp';
              if (n.endsWith('.gif')) return 'image/gif';
              return '';
            })();
            return {
              delivery_id: d.delivery_id || d.c_no,

              c_no: d.c_no || d.delivery_id,
              courier: d.courier || d.courier_name,
              date: d.date_formatted || d.date || d.delivery_date,
              contact_name: d.name || d.contact_name || "-",
              contact_mobile: d.mobile || d.contact_mobile || "-",
              tracking_id: d.tracking_id || d.tracking_no,
              track_url: d.track_url || d.tracking_link,
              file_url: fileUrl || '',
              file_url_alt: fileUrlAlt || '',
              file_url_alt2: fileUrlAlt2 || '',
              file_url_candidates: candidates,
              mime: d.mime || '',
              mime_guess: mimeGuess,
              status: d.status,
            };
          });
          setCourierTracking(courierList);
        } else if (Array.isArray(result.data.deliveries)) {
          const courierList = result.data.deliveries.map((d) => {
            const fileName = d.name || d.file_name || d.filename || d.invoice_name || '';
            const relPath = d.invoice || d.pdf_file || '';
            const candidates = [];
            const pushUniq = (u) => { if (u && !candidates.includes(u)) candidates.push(u); };
            if (relPath) {
              pushUniq(`${API_ROOT}/${String(relPath).replace(/^\/+/, '')}`);
            }
            if (fileName) {
              pushUniq(`${API_ROOT}/uploads/delivery/${encodeURIComponent(fileName)}`);
              pushUniq(`${API_ROOT}/uploads/delivery/${fileName}`);
            }
            const fileUrl = candidates[0] || '';
            const fileUrlAlt = candidates[1] || '';
            const fileUrlAlt2 = candidates[2] || '';
            const mimeGuess = (() => {
              const n = (fileName || relPath || '').toLowerCase();
              if (n.endsWith('.pdf')) return 'application/pdf';
              if (n.endsWith('.png')) return 'image/png';
              if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
              if (n.endsWith('.webp')) return 'image/webp';
              if (n.endsWith('.gif')) return 'image/gif';
              return '';
            })();
            return {
              c_no: d.delivery_id,
              courier: d.courier_name,
              date: d.date_formatted || d.delivery_date,
              contact_name: d.contact_name || "-",
              contact_mobile: d.contact_mobile || "-",
              tracking_id: d.tracking_no,
              track_url: d.tracking_link,
              file_url: fileUrl || '',
              file_url_alt: fileUrlAlt || '',
              file_url_alt2: fileUrlAlt2 || '',
              file_url_candidates: candidates,
              mime: d.mime || d.file_mime || d.mimetype || '',
              mime_guess: mimeGuess,
              status: d.status,
            };
          });

          setCourierTracking(courierList);
        }
        if (Array.isArray(result.data.videos)) {
          setVideos(result.data.videos);
        }
      } else {
        setError(result.message || result.msg || "No patient data found.");
      }
    } catch (err) {
      console.error("Fetch patient details error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }

  };

  const handleDeleteInvoice = async (invoice_id) => {
    if (!invoice_id) {
      toast.error("Invoice ID not found");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this invoice?")) return;

    try {
      const token = (localStorage.getItem("session_token") || "").trim();
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }

      const url = `https://newerp.awasthivitality.com/api/invoices/${invoice_id}?token=${encodeURIComponent(token)}`;

      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Session-Token": token,
        },
      });

      if (response.data?.success) {
        toast.success(response.data.msg || "Invoice deleted successfully");

        // 🔥 REMOVE invoice from UI immediately
        setInvoices((prev) =>
          prev.filter((inv) => inv.invoice_id !== invoice_id)
        );
      } else {
        toast.error(response.data?.msg || "Failed to delete invoice");
      }
    } catch (err) {
      console.error("Delete invoice error:", err);
      toast.error(
        err.response?.data?.msg || "Server error while deleting invoice"
      );
    }
  };

  useEffect(() => {
    // Always fetch patient details (view permission not required for this page)
    const controller = new AbortController();
    (async () => {
      try {
        await fetchPatientDetails();
      } catch { /* ignored */ }
    })();
    return () => controller.abort();
  }, [patient_id]);
  // Remove duplicate invoice fetch - invoices are already included in main API response
  // useEffect removed to avoid duplicate API call
  const reloadReports = async () => {
    try {
      const token = localStorage.getItem("session_token") || "";
      const res = await fetch(
        `https://newerp.awasthivitality.com/api/patient/view/${patient_id}?token=${encodeURIComponent(token)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Session-Token": token,
          },
        }
      );

      const data = await res.json();
      if (data.success && data.data?.reports?.documents) {
        setReports(data.data.reports.documents);
      }
    } catch (err) {
      console.error("Failed to reload reports", err);
    }
  };

  const handleStatusUpdate = async (deliveryId, newStatus) => {
    if (!deliveryId) {
      toast.error("Critical Error: Real Delivery ID not found!");
      return;
    }

    try {
      // We send the REAL delivery_id to the URL
      const url = `https://newerp.awasthivitality.com/public/api/courier-status/${deliveryId}?status=${newStatus}`;
      
      const response = await axios.post(
        url,
        { _method: 'PUT' }, 
        {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Status updated successfully!");
      } else {
        toast.error(response.data.msg || "Update failed");
      }
    } catch (err) {
      console.error("Update error:", err.response?.data);
      toast.error("Server Error: Check if delivery_id is valid");
    }
  };

  // Wait for permissions to load before showing anything
  if (!permissionsLoaded) {
    return <p>Loading permissions...</p>;
  }

  // Show access denied if user doesn't have permission
  if (!canView) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          padding: '40px',
          background: '#fff',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>
            ❌ Access Denied
          </h2>
          <p style={{ fontSize: '16px', color: '#666' }}>
            You do not have permission to view patient details.
          </p>
          <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>
            Required permission: operation.patient or operation.registered_patient
          </p>
        </div>
      </div>
    );
  }

  if (error) return <p>{error}</p>;
  if (!patient) return <p>No patient data available.</p>;

  return (
    <div className="layout-container">


{showPrintModal && (
  <div className="print-modal-overlay">
    <div className="print-modal">
      <h3 >🖨️ Print Prescription</h3>

      <p style={{ color: "#555", marginBottom: "10px" }}>
        You can choose a prescription date.
        <br />
        <small>(If not selected, today’s date will be used automatically)</small>
      </p>

      <input
        type="date"
        value={prescriptionDate}
        onChange={(e) => setPrescriptionDate(e.target.value)}
        className="print-date-input"
      />

      <div className="print-modal-actions">
        <button
          className="btn-cancel"
          onClick={() => {
            setShowPrintModal(false);
            setPrescriptionDate("");
          }}
        >
          Cancel
        </button>

        <button
          className="btn-print "
          onClick={() => {
            handlePrintPrescription(prescriptionDate);
            setShowPrintModal(false);
            setPrescriptionDate("");
          }}
        >
          Print Now
        </button>
      </div>
    </div>
  </div>
)}


      <div className="patient-layout">
        {/* LEFT PANEL */}
        <div className="left-panel">
          <div className="card">
<div
  className="card-header"
  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "11px",
    }}
  >
    <h3 style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
      <User size={16} /> Patient Details
    </h3>
  </div>

  {/* Right side actions */}
  <div style={{ display: "flex", alignItems: "center" }}>
    
    {/* Edit Button */}
    <button
      onClick={() => navigate(`/patient/edit/${patient.patient_id}`)}
      className="action-btn edit"
      title="Edit"
      style={{
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: "#2fb2e7",
        display: "flex",
        alignItems: "center"
      }}
    >
      <Pencil size={18} />
    </button>

    {/* PR Number */}
    <span style={{ color: "red", fontWeight: "bold" }}>
      {patient.pr_no}
    </span>

  </div>
</div>

            <div className="patient-grid">
              {/* <p><b>PR:</b> {patient.pr_no}</p> */}
              <p><b>Name:</b> {patient.name} ({patient.age}Y/{patient.sex_text})</p>
              <p>
                <b>Mob:</b>{" "}
                <span
                  onClick={() => copyNumber(patient.mobile, "mobile")}
                  style={{ cursor: "pointer" }}
                >
                  {maskPhoneNumber(patient.mobile)}
                </span>

                {copiedField === "mobile" && (
                  <span style={{ marginLeft: 8, color: "green", fontSize: "12px" }}>
                    Copied
                  </span>
                )}
              </p>


              <p>
                <b>WA:</b>{" "}
                <span
                  onClick={() => copyNumber(patient.mobile2, "wa")}
                  style={{ cursor: "pointer" }}
                >
                  {maskPhoneNumber(patient.mobile2)}
                </span>

                {copiedField === "wa" && (
                  <span style={{ marginLeft: 8, color: "green", fontSize: "12px" }}>
                    Copied
                  </span>
                )}
              </p>


              {/* <p><b>Email:</b> {patient.email || "N/A"}</p> */}
              <p><b>Ht:</b> {patient.height || "N/A"}</p>
              <p><b>Wt:</b> {patient.weight || "N/A"}</p>
              <p style={{ color: patient.bp_text.toLowerCase() === "yes" ? "red" : "inherit" }}>
                <b>BP:</b> {patient.bp_text}
              </p>

              <p style={{ color: patient.diabetes_text.toLowerCase() === "yes" ? "red" : "inherit" }}>
                <b>DM:</b> {patient.diabetes_text}
              </p>

              <p style={{ color: patient.thyroid === 1 ? "red" : "inherit" }}>
                <b>Thy:</b> {patient.thyroid === 1 ? "Yes" : "No"}
              </p>

              <p><b>Branch:</b> {patient.branch_name || "N/A"}</p>
      {/* <div style={{ textAlign: "center", marginBottom: "15px" }}>
  {patient?.patient_photo ? (
    <img
      src={patient.patient_photo}
      alt="Patient"
      style={{ width: "120px", height: "120px", borderRadius: "8px", objectFit: "cover" }}
    />
  ) : (
    <div>No Photo</div>
  )}
</div> */}
<p>
  <b>Pt Pic:</b>{" "}
  {patient?.patient_photo ? (
    <>
      <a
        href={patient.patient_photo}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#2fb2e7", textDecoration: "underline", marginRight: "10px" }}
      >
        View
      </a>

 <button
  onClick={() => {
    const confirmDelete = window.confirm("Are you sure you want to delete this photo?");
    if (confirmDelete) {
      handleDeletePhoto();
    }
  }}
  style={{
    color: "#2fb2e7",
    border: "none",
    background: "none",
    padding: "8px 14px",
    fontSize: "13px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    transition: "0.3s"
  }}
>
 🗑️ Delete
</button>
    </>
  ) : (
    "N/A"
  )}
</p>
              {/* <p><b>Assign:</b> {patient.patient_assign || "N/A"}</p> */}
              {/* <p><b>Mode:</b> {patient.service_type || "N/A"}</p> */}
            </div>
          </div>


          <div className="card">
            <h3>
              <MapPin size={16} /> Permanent Address
            </h3>
            <p><b>City:</b> {patient.city}</p>
            <p><b>State:</b> {patient.state}</p>
            <p><b>Country:</b> {patient.country}</p>
          </div>

          {/* REPORTS SECTION */}
          <div className="card">
            {canViewReport && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3>Reports</h3>
                {canAddReport && (
                  <button
                    className="uploaded-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    +
                  </button>
                )}

                <input
  type="file"
  ref={fileInputRef}
  accept="image/*,application/pdf"
  multiple
  style={{ display: "none" }}
  onChange={handleFileChange}
/>

              </div>
            )}

            <div className="reports-container" style={{ position: "relative" }}>
              {reports.length > 0 ? (
                reports.map((report, index) => {
                  const baseHosts = [
                    'https://newerp.awasthivitality.com/'
                  ];
                  const fileName = report.file_name || '';
                  const primaryUrl = report.file_url || '';
                  const candidates = [];
                  const pushUniq = (u) => { if (u && !candidates.includes(u)) candidates.push(u); };
                  const ensureAbs = (root, rel) => `${root}/${String(rel).replace(/^\/+/, '')}`;

                  // 1) Primary URL (as-is and encoded)
                  if (primaryUrl) {
                    pushUniq(primaryUrl);
                    try { pushUniq(encodeURI(primaryUrl)); } catch { }
                    // If relative path, try on known roots (with and without public)
                    if (!/^https?:\/\//i.test(primaryUrl)) {
                      baseHosts.forEach((h) => {
                        pushUniq(ensureAbs(h, primaryUrl));
                        pushUniq(ensureAbs(h, `public/${primaryUrl}`));
                      });
                    }
                  }

                  // 2) Derived from fileName under uploads/delivery (both encoded and raw)
                  if (fileName) {
                    const encodedName = encodeURIComponent(fileName);
                    baseHosts.forEach(h => {
                      pushUniq(`${h}/uploads/delivery/${encodedName}`);
                      pushUniq(`${h}/uploads/delivery/${fileName}`);
                      pushUniq(`${h}/uploads/patient_mri/${encodedName}`);
                      pushUniq(`${h}/uploads/patient_mri/${fileName}`);
                    });
                  }
                  const href = candidates[0] || '';
                  const isPdf = /\.pdf(\?|$)/i.test((fileName || primaryUrl || '').toLowerCase());
                  return (
                    <div
                      key={index}
                      className="report-item"
                      style={{
                        display: "inline-block",
                        position: "relative",
                        margin: "10px",
                        textAlign: "center",
                      }}
                    >
                  {isPdf ? (
  <div style={{ width: 140 }}>
    <div
      style={{
        width: 100,
        height: 100,
        border: '1px solid #ddd',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa',
        fontSize: 12,
        color: '#d9534f',
        cursor: href ? 'pointer' : 'default'
      }}
      title={href ? 'Open PDF' : 'File not found'}
      onClick={() => {
        if (!href) return;
        window.open(href, "_blank", "noopener,noreferrer");
      }}
    >
      PDF
    </div>
  </div>
) : (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <img
                            src={href}
                            alt={report.file_name}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              border: "1px solid #e1e1e1",
                            }}
                            onLoad={(e) => {
                              const img = e.currentTarget;
                              const anchor = img.closest('a');
                              if (anchor && anchor.href !== img.src) anchor.href = img.src;
                            }}
                            onError={(e) => {
                              const img = e.currentTarget;
                              const step = parseInt(img.dataset.step || '0', 10);
                              const nextSrc = candidates[step + 1] || '';
                              if (nextSrc) {
                                img.dataset.step = String(step + 1);
                                img.src = nextSrc;
                                const anchor = img.closest('a');
                                if (anchor) anchor.href = nextSrc;
                              } else {
                                img.style.display = 'none';
                                const msg = document.createElement('div');
                                msg.style.fontSize = '11px';
                                msg.style.color = '#666';
                                msg.textContent = 'File not found';
                                img.parentElement && img.parentElement.appendChild(msg);
                              }
                            }}
                          />
                        </a>
                      )}

                      {/* Delete Button - only visible if user has report delete permission */}
                      {canDeleteReport && (
                        <button
                          onClick={() => handleDeleteReport(report.doc_id || report.id)}
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            background: "#dc3545",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: "26px",
                            height: "26px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: "16px",
                            fontWeight: "bold",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                            transition: "all 0.2s ease",
                          }}
                          title="Delete report"
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#c82333")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#dc3545")}
                        >
                          ×
                        </button>
                      )}

                      <p
                        style={{
                          fontSize: "12px",
                          marginTop: "5px",
                          maxWidth: "80px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {report.file_name}
                      </p>
                    </div>
                  );
                })
              ) : (
                <p>No reports available for this patient.</p>
              )}
            </div>

            {/* Preview & Confirm Upload */}
            {confirmUpload && (
              <div style={{ marginTop: "15px" }}>
                <h4>Preview</h4>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {previewUrls.map((url, i) =>
                    url ? (
                      <img
                        key={i}
                        src={url}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 6,
                        }}
                      />
                    ) : (
                      <div
                        key={i}
                        style={{
                          width: 80,
                          height: 80,
                          border: "1px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                        }}
                      >
                        PDF
                      </div>
                    )
                  )}
                </div>

                <div style={{ marginTop: 10 }}>
                  {confirmUpload && (
                    <button onClick={handleUpload} className="upload-btn">
                      {selectedFiles.length > 1 ? "Upload All" : "Upload"}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedFiles([]);
                      setPreviewUrls([]);
                      setConfirmUpload(false);
                    }}
                    style={{ background: "red", color: "#fff", marginLeft: 10 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

          </div>

          <div className="card relatives-section">
            <div className="relatives-header">
              <h3 className="relatives-title">
                <Users size={18} className="icon" /> Relatives
              </h3>
              <button className="btn-add-new" onClick={handleAddNewClick}>
                + Add New
              </button>
            </div>

            {/* ✅ Display Relatives - use data from main API response */}
            <RelativesList
              patientId={patient_id}
              initialRelatives={relativesFromAPI}
            />
          </div>


        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel">

          <div className="header">
            <h3>Course Information</h3>

            <div className="actions" style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
              {/* Existing Buttons */}

              {/* ✅ New Buttons on Right Side */}
              {canSeeNotepad && (
                <button
                  className="btn btn-notepad"
                  onClick={() => navigate(`/patient/${patient_id}/add-note`)}
                >
                  Add Notes
                </button>
              )}


              <button
                className="btn btn-local"
                onClick={() => navigate(`/patient/${patient_id}/LocalNote`)}

              // onClick={() => navigate("/LocalNotepad")}
              >
                Add Local
              </button>

              <button
                onClick={() => setShowPrintModal(true)}
                style={{
                  backgroundColor: "#ff6200",
                  color: "#fff",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <FaPrint /> Print Prescription
              </button>



              <button
                className="btn btn-new"
                onClick={() => navigate(`/AddCourse/${patient_id}`)}
              >
                Add Course
              </button>
              <button
                className="btn btn-invoice"
                onClick={() => navigate(`/AddInvoice/${patient_id}`)}
              >
                Add Invoice
              </button>
              <button
                className="btn btn-back"
                onClick={() => navigate("/PatientList")}
                title="Back to Patient List"
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>

            </div>
          </div>

<div className="table-responsive">

          <table className="course-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th
                  onClick={() =>
                    setDateSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Date{" "}
                  <span style={{ fontSize: "12px" }}>
                    {dateSortOrder === "asc" ? "⬆️" : "⬇️"}
                  </span>
                </th>

                <th>Service Type</th>
                <th>Diagnosis</th>
                <th>Sign & Symptom</th>
                <th>Medicine</th>
                <th>Diagnose Code</th>

                <th>Ageing (Days)</th>
                <th>Dosing</th>
                <th>Visit Remark</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {courses.length > 0 ? (
                (() => {
                  let serial = 1; // SERIAL COUNTER HERE
                  return sortedCourses.flatMap((c, i) => {
                    return [
                      // Main diagnosis row
                      <tr key={c.patient_course_id || i}>
                        <td>{serial++}</td>
                        <td>{c.date_formatted || c.course_date}</td>
                        <td>{c.service_type}</td>
                        <td>{c.diagnosis}</td>
                        <td>{c.symptoms_display || "—"}</td>
                        <td>{Array.isArray(c.medicine_names) ? c.medicine_names.join(", ") : c.medicine_names}</td>
                        <td>{c.diagnosis_code}</td>
                        <td>
                          {c.ageing_days !== undefined && c.ageing_days !== null
                            ? c.ageing_days
                            : "—"}
                        </td>
                        <td>{c.remark || "—"}</td>
                        <td>
                          <span style={{ color: "red" }}>
                            {c.remark4 || patient.remark4 || "—"}
                          </span>
                        </td>
                        <td
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            alignItems: "center",
                          }}
                        >
                          {c.patient_course_id && canEdit && (
                            <button
                              title="Edit"
                              onClick={() => handleEdit(c.patient_course_id)}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#f1c40f",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                            >
                              ✏️
                            </button>
                          )}

                          {c.patient_course_id && canDelete && (
                            <button
                              title="Delete"
                              onClick={() => handleDelete(c.patient_course_id)}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#e74c3c",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                            >
                              🗑️
                            </button>
                          )}

                          {c.patient_course_id1 && canEdit && (
                            <button
                              title="Edit"
                              onClick={() => handleEdit(c.patient_course_id1)}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#f39c12",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                            >
                              ✏️
                            </button>
                          )}

                          {c.patient_course_id1 && canDelete && (
                            <button
                              title="Delete"
                              onClick={() => handleDelete(c.patient_course_id1)}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#c0392b",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                color: "#fff",
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      </tr>,

                      // Extra row for diagnosis1
                      c.diagnosis1 && (
                        <tr
                          key={c.patient_course_id + "_extra"}
                          style={{ backgroundColor: "#f9f9f9" }}
                        >
                          <td>{serial++}</td>
                          <td>{c.date_formatted || c.course_date}</td>
                          <td>{c.service_type}</td>
                          <td>{c.diagnosis1}</td>
                          <td>{c.symptoms1_display || "—"}</td>
                          <td>{c.medicine_names1 || "—"}</td>
                          <td>
                            {c.ageing_days !== undefined && c.ageing_days !== null
                              ? c.ageing_days
                              : "—"}
                          </td>
                          <td>{c.remark1 || "—"}</td>
                          <td>{c.remark4_1 || patient.remark4_1 || "—"}</td>
                          <td
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                              alignItems: "center",
                            }}
                          >
                            {canEdit && (
                              <button
                                title="Edit"
                                onClick={() => handleEdit(c.patient_course_id)}
                                style={{
                                  padding: "6px 12px",
                                  backgroundColor: "#f1c40f",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  color: "#fff",
                                }}
                              >
                                ✏️
                              </button>
                            )}
                            {canDelete && (
                              <button
                                title="Delete"
                                onClick={() => handleDelete(c.patient_course_id)}
                                style={{
                                  padding: "6px 12px",
                                  backgroundColor: "#e74c3c",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  color: "#fff",
                                }}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      ),
                    ];
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center" }}>
                    No course information available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <div className="header" style={{ marginTop: "30px" }}>
            <h3>Invoice Information</h3>
          </div>

          <table className="course-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Date</th>
                <th>Patient Name</th>
                <th>tnx</th>
                <th>Total Amount</th>
                <th>Created By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length > 0 ? (
                invoices.map((inv, i) => (
                  <tr key={inv.invoice_id || i}>
                    <td>{inv.invoice_no}</td>
                    <td>{inv.invoice_date}</td>
                    <td>{patient?.name || inv.patient_name}</td>
                    <td>{inv.invoice_comment}</td>
                    <td>₹{inv.invoice_amount}</td>
                    <td>{inv.created_by || "N/A"}</td>
                    <td>
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: inv.pay_status === 'Paid' ? '#28a745' : '#ffc107',
                        color: inv.pay_status === 'Paid' ? 'white' : 'black',
                        fontSize: "12px"
                      }}>
                        {inv.pay_status}
                      </span>
                    </td>
                    <td style={{ display: "flex", gap: "6px" }}>
                      {/* 
                        In this page, use permission keyvalue: 'invoice'->'editview'
                        Show edit button only if user has editview permission
                        (the backend logic for allowed permissions is in AdminController::getAllowedPermissionsMap)
                      */}
                      {canEditInvoice && (
                        <button
                          className="btn-icon edit"
                          onClick={() => navigate(`/EditPatientInvoice/${inv.invoice_id}`)}
                          title="Edit Invoice"
                        >
                          <FaEdit />
                        </button>
                      )}

                      {/* PRINT/VIEW BUTTON - always visible */}
                      <button
                        className="btn-icon print"
                        title="View/Print Invoice"
                        onClick={() => navigate(`/InvoicePrint/${inv.invoice_id}`)}
                      >
                        <FaPrint />
                      </button>
                      {/* ✅ DELETE BUTTON - only visible if user has invoicedeleteview permission */}
                      {canDeleteInvoice && (
                        <button
                          onClick={() => handleDeleteInvoice(inv.invoice_id)}
                          title="Delete Invoice"
                          style={{
                            // background: "#dc3545",
                            color: "#ac2e2e",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 10px",
                            cursor: "pointer",
                          }}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No invoice information available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="header" style={{ marginTop: "30px" }}>
            <h3>Courier Information</h3>
          </div>
          <form className="row g-2 mb-3" onSubmit={handleCreateDelivery}>
          </form>
          <table className="course-table">

            <thead>
              <tr>
                <th>C.No</th>
                <th>Courier</th>
                <th>Date</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Tracking Id</th>
                <th>Customer Details</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {courierTracking.length > 0 ? (
                courierTracking.map((c, i) => (
                  <tr key={c.tracking_id || i}>
                    <td>{c.c_no}</td>
                    <td>{c.courier}</td>
                    <td>{c.date}</td>
                    <td>{c.contact_name}</td>
                    <td>{maskPhoneNumber(c.contact_mobile)}</td>
                    <td>
                      {c.tracking_id}{" "}

                      <a href={`https://leadurl.awasthivitality.com/tracking-view.php?awb=${encodeURIComponent(c.tracking_id || '')}`}
  target="_blank"> Track </a> &nbsp; | &nbsp; 

                      <a href={`https://leadurl.awasthivitality.com/label-generation.php?awb=${encodeURIComponent(c.tracking_id || '')}`}
  target="_blank"> Print Label </a>
                    </td>
                    <td>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer"
                        }}
                        onClick={() => window.open(c.file_url, "_blank", "noopener,noreferrer")}
                      >
                        View Details
                      </button>
                    </td>


                    <td style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <select
                        value={c.status}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setCourierTracking((prev) =>
                            prev.map((item) =>
                              // Match by c_no but update the whole object state
                              item.c_no === c.c_no ? { ...item, status: newValue } : item
                            )
                          );
                        }}
                        style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        // ✅ Change c.c_no to c.delivery_id here
                        onClick={() => handleStatusUpdate(c.delivery_id, c.status)} 
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#3498db",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        Status Update
                      </button>

                      <span style={{ fontSize: "10px", color: "#2c3e50" }}>
                        {currentStatusMap[c.status]}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDeleteCourier(c.delivery_id)}
                        style={{
                          // padding: "6px 10px",
                          // backgroundColor: "#e74c3c",
                          // color: "#fff",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                        title="Delete Courier"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    No courier information available
                  </td>
                </tr>
              )}
            </tbody>

          </table>
          {selectedCustomer && (
            <div
              style={{
                position: "fixed",
                top: 0, left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
              onClick={() => setSelectedCustomer(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "400px",
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                }}
              >
                <h3>Customer Details</h3>

                <p><b>Name:</b> {selectedCustomer.contact_name}</p>
                <p><b>Mobile:</b> {selectedCustomer.contact_mobile}</p>
                <p><b>Address:</b> {selectedCustomer.address}</p>
                <p><b>Courier:</b> {selectedCustomer.courier}</p>
                <p><b>Tracking ID:</b> {selectedCustomer.tracking_id}</p>

                <button
                  onClick={() => setSelectedCustomer(null)}
                  style={{
                    marginTop: "10px",
                    padding: "8px 12px",
                    background: "#3498db",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* ✅ INVOICE INFORMATION SECTION */}
          {/* VIDEOS SECTION */}

        </div>


      </div>
    </div>
  );
};

export default PatientDetails;